/* @flow */

/**
 * WARNING: because of how routes are registered in `useRegistrationRoute`,
 * this file should only export `RouteData` types.
 *
 * The not found route MUST be the last one defined.
 */

import { either } from 'ramda';

// Utils
import { isVirtualBraindateOverlayShown } from 'plugin/virtual-braindate/selector/virtualBraindateSelectors';
import {
  standardAuthenticatedPageRedirect,
  standardUnauthenticatedPageRedirect,
  redirectToHomeWhenSignupDisabled,
  redirectToHomeWhenSignupCompleted,
  redirectWhenRedirectAndAuthenticated,
  redirectToSignupUnlessCompleted,
  redirectToLoginIfOnboardingCompletedOnDevice,
  redirectToLoginURL,
} from 'src/shared/app/base/route/util/redirectUtils';
import { getBraindateSlug } from 'src/shared/app/base/route/util/routeUtils';
import {
  isMarketShown,
  isSplashScreenShown,
  isOnboardingScreenShown,
  isPermissionScreenShown,
} from 'src/shared/app/base/route/util/routeUtilsSelectors';

// Templates
import BlankTemplate from 'src/shared/app/base/component/template/BlankTemplate';
import BlankWithDesktopSideBarTemplate from 'src/shared/app/base/component/template/BlankWithDesktopSideBarTemplate';
import WithSidebarTemplate from 'src/shared/app/base/component/template/WithSidebarTemplate';
import StretchedWidthTemplate from 'src/shared/app/base/component/template/StretchedWidthTemplate';
import FullWidthTemplate from 'src/shared/app/base/component/template/FullWidthTemplate';
import FullscreenTemplate from 'src/shared/app/base/component/template/FullscreenTemplate';

// Pages
import LazyLightMain from 'plugin/light/component/LazyLightMain';
import LazyBlankMain from 'src/shared/app/blank/LazyBlankMain';
import LazyProfileMain from 'src/shared/app/account/base/component/LazyProfileMain';
import LazyOwnTopicsMain from 'src/shared/app/account/base/component/LazyOwnTopicsMain';
import LazyAvaibilityMain from 'src/shared/app/account/base/component/LazyAvaibilityMain';
import LazyProfileEditMain from 'src/shared/app/account/profile/component/edit/LazyProfileEditMain';
import LazyLoginMain from 'src/shared/app/authentication/component/login/LazyLoginMain';
import LazyResetPasswordMain from 'src/shared/app/authentication/component/reset-password/LazyResetPasswordMain';
import LazyLoginSupportMain from 'src/shared/app/authentication/component/support/login/LazyLoginSupportMain';
import LazyPasswordSupportMain from 'src/shared/app/authentication/component/support/password/LazyPasswordSupportMain';
import LazyRetrieveUsernameMain from 'src/shared/app/authentication/component/support/login/username/LazyRetrieveUsernameMain';
import LazyLoginLinkMain from 'src/shared/app/authentication/component/support/login/link/LazyLoginLinkMain';
import LazySupportContactMain from 'src/shared/app/authentication/component/support/contact/LazySupportContactMain';
import LazyBraindateMain from 'src/shared/app/braindate/component/LazyBraindateMain';
import LazyBraindatesMain from 'src/shared/app/braindate/component/main/LazyBraindatesMain';
import LazyConversationMain from 'src/shared/app/conversation/component/LazyConversationMain';
import LazyClientErrorMain from 'src/shared/app/error/component/LazyClientErrorMain';
import LazyServerErrorMain from 'src/shared/app/error/component/LazyServerErrorMain';
import LazyFAQMain from 'src/shared/app/faq/component/LazyFAQMain';
import LazyMarketMain from 'src/shared/app/market/component/MarketMain';
import LazyNotFoundMain from 'src/shared/app/not-found/component/LazyNotFoundMain';
import LazyOnboardingMain from 'src/shared/app/onboarding/component/LazyOnboardingMain';
import LazySplashScreen from 'src/shared/app/onboarding/component/splash/LazySplashScreen';
import LazyPermissionMain from 'src/shared/app/permission/component/LazyPermissionMain';
import LazyPreferencesMain from 'src/shared/app/preference/component/LazyPreferencesMain';
import LazySignupMain from 'src/shared/app/signup/component/LazySignupMain';
import LazySupportMain from 'src/shared/app/support/component/LazySupportMain';
import LazyNewTopicMain from 'src/shared/app/topic/new/component/LazyNewTopicMain';
import LazyNewTopicNoPermissionMain from 'src/shared/app/topic/new/component/LazyNewTopicNoPermissionMain';
import LazyUserMain from 'src/shared/app/user/component/LazyUserMain';
import LazyInviteMain from 'src/shared/app/user/component/LazyInviteMain';
import LazyTopicDetailsMain from 'src/shared/components/domain/topic/details/LazyTopicDetailsMain';
import LazyTopicEditMain from 'src/shared/components/domain/topic/form/LazyTopicEditMain';
import LazyOauthRedirect from 'src/shared/app/account/oauth/components/LazyOauthRedirect';
import LazyConnectOnInterest from 'src/shared/app/connect-on-interests/component/LazyConnectOnInterest';
import LazyNewExpressionOfGratitude from 'src/shared/app/expression-of-gratitude/component/new/LazyNewExpressionOfGratitude';
import LazyExpressionOfGratitudeDetails from 'src/shared/app/expression-of-gratitude/component/details/LazyExpressionOfGratitudeDetails';
import { getEventSafely } from 'src/shared/domain/event/selector/eventSelectors';
import { loginPath } from 'src/shared/app/base/route/setting/routePaths';

// Types
import type { State } from 'src/shared/core/type/reduxTypes';

export const homeRoute = {
  path: '/',
  template: (state: State, { areEmailNotificationsEnabled }: Object) => {
    if (isSplashScreenShown(state, getEventSafely(state))) {
      return BlankTemplate;
    }

    if (isOnboardingScreenShown(state)) {
      return BlankTemplate;
    }

    if (isPermissionScreenShown(state, areEmailNotificationsEnabled)) {
      return BlankWithDesktopSideBarTemplate;
    }

    return WithSidebarTemplate;
  },
  main: (state: State, { areEmailNotificationsEnabled }: Object) => {
    if (isSplashScreenShown(state, getEventSafely(state))) {
      return LazySplashScreen;
    }

    if (isOnboardingScreenShown(state)) {
      return LazyOnboardingMain;
    }

    if (isPermissionScreenShown(state, areEmailNotificationsEnabled)) {
      return LazyPermissionMain;
    }

    return LazyMarketMain;
  },
  help: isMarketShown,
  redirect: either(
    redirectWhenRedirectAndAuthenticated,
    either(
      redirectToSignupUnlessCompleted,
      redirectToLoginIfOnboardingCompletedOnDevice,
    ),
  ),
};

export const peopleFeedRoute = {
  path: '/people/',
  template: WithSidebarTemplate,
  main: LazyMarketMain,
  help: true,
  redirect: standardAuthenticatedPageRedirect,
};

export const signupRoute = {
  path: '/signup/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazySignupMain,
  redirect: either(
    redirectToHomeWhenSignupDisabled,
    redirectToHomeWhenSignupCompleted,
  ),
  scrollToTop: true,
};

export const loginRoute = {
  path: loginPath,
  template: BlankWithDesktopSideBarTemplate,
  main: LazyLoginMain,
  redirect: redirectToLoginURL,
  scrollToTop: true,
  allowAbsoluteURLRedirect: true,
};

export const loginSupportRoute = {
  path: '/login-support/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyLoginSupportMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const retrieveUsernameRoute = {
  path: '/login-support/username/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyRetrieveUsernameMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const loginLinkRoute = {
  path: '/login-support/link/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyLoginLinkMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const contactRoute = {
  path: '/support-contact/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazySupportContactMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const passwordSupportRoute = {
  path: '/password-support/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyPasswordSupportMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const resetPasswordRoute = {
  path: '/reset-password/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyResetPasswordMain,
  redirect: standardUnauthenticatedPageRedirect,
  scrollToTop: true,
};

export const topicRoute = {
  path: '/topics/:id/',
  template: StretchedWidthTemplate,
  main: LazyTopicDetailsMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const topicEditRoute = {
  path: '/topics/:id/edit/',
  template: StretchedWidthTemplate,
  main: LazyTopicEditMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const topicInviteRoute = {
  path: '/topics/:id/invite/:userId',
  template: WithSidebarTemplate,
  main: LazyTopicDetailsMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const newTopicRoute = {
  path: '/new-topic/',
  template: FullscreenTemplate,
  main: LazyNewTopicMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const newTopicNoPermissionRoute = {
  path: '/new-topic-no-permission/',
  template: WithSidebarTemplate,
  main: LazyNewTopicNoPermissionMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const braindatesRoute = {
  path: (state: State) => `/${getBraindateSlug(getEventSafely(state))}/`,
  template: FullWidthTemplate,
  main: LazyBraindatesMain,
  redirect: standardAuthenticatedPageRedirect,
  help: true,
};

export const pendingBraindatesRoute = {
  path: (state: State) =>
    `/${getBraindateSlug(getEventSafely(state))}/pending/`,
  template: FullWidthTemplate,
  main: LazyBraindatesMain,
  redirect: standardAuthenticatedPageRedirect,
  help: true,
};

export const confirmedBraindatesRoute = {
  path: (state: State) =>
    `/${getBraindateSlug(getEventSafely(state))}/confirmed/`,
  template: (_state: Object, { hasSidebar }: Object) =>
    hasSidebar ? WithSidebarTemplate : FullWidthTemplate,
  main: LazyBraindatesMain,
  redirect: standardAuthenticatedPageRedirect,
  help: true,
};

export const pastBraindatesRoute = {
  path: (state: State) => `/${getBraindateSlug(getEventSafely(state))}/past/`,
  template: (_state: Object, { hasSidebar }: Object) =>
    hasSidebar ? WithSidebarTemplate : FullWidthTemplate,
  main: LazyBraindatesMain,
  redirect: standardAuthenticatedPageRedirect,
  help: true,
};

export const expressionOfGratitudeRoute = {
  path: '/expression-of-gratitude/',
  template: (_state: Object, { hasSidebar }: Object) =>
    hasSidebar ? WithSidebarTemplate : FullWidthTemplate,
  main: LazyBraindatesMain,
  redirect: standardAuthenticatedPageRedirect,
};

export const expressionOfGratitudeDetailsRoute = {
  path: '/expression-of-gratitude/:id',
  template: StretchedWidthTemplate,
  main: LazyExpressionOfGratitudeDetails,
  redirect: standardAuthenticatedPageRedirect,
};

export const newExpressionOfGratitudeRoute = {
  path: '/expression-of-gratitude/grant/',
  template: StretchedWidthTemplate,
  main: LazyNewExpressionOfGratitude,
  redirect: standardAuthenticatedPageRedirect,
};

export const newExpressionOfGratitudeWithBraindateRoute = {
  path: '/expression-of-gratitude/grant/:braindateId',
  template: StretchedWidthTemplate,
  main: LazyNewExpressionOfGratitude,
  redirect: standardAuthenticatedPageRedirect,
};

export const newExpressionOfGratitudeWithBraindateAndUserRoute = {
  path: '/expression-of-gratitude/grant/:braindateId/:userId',
  template: StretchedWidthTemplate,
  main: LazyNewExpressionOfGratitude,
  redirect: standardAuthenticatedPageRedirect,
};

export const braindateRoute = {
  path: (state: State) => `/${getBraindateSlug(getEventSafely(state))}/:id/`,
  template: StretchedWidthTemplate,
  main: LazyBraindateMain,
  help: (state: State) => !isVirtualBraindateOverlayShown(state),
  redirect: standardAuthenticatedPageRedirect,
};

export const conversationRoute = {
  path: '/topics/:id/conversation/',
  template: StretchedWidthTemplate,
  main: LazyConversationMain,
  redirect: standardAuthenticatedPageRedirect,
};

export const preferencesRoute = {
  path: '/preferences/',
  template: StretchedWidthTemplate,
  main: LazyPreferencesMain,
  help: true,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const userRoute = {
  path: '/users/:id/',
  template: StretchedWidthTemplate,
  main: LazyUserMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const userInviteRoute = {
  path: '/users/:id/invite',
  template: StretchedWidthTemplate,
  main: LazyInviteMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const connectOnInterestRoute = {
  path: '/users/:userId/interest/:interest',
  template: BlankTemplate,
  main: LazyConnectOnInterest,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const profileRoute = {
  path: '/account/profile/',
  template: StretchedWidthTemplate,
  main: LazyProfileMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
  help: true,
};

export const ownTopicsRoute = {
  path: '/account/topics/',
  template: StretchedWidthTemplate,
  main: LazyOwnTopicsMain,
  help: true,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const ownTopicsActiveRoute = {
  path: '/account/topics/active',
  template: StretchedWidthTemplate,
  main: LazyOwnTopicsMain,
  help: true,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const ownTopicsHiddenRoute = {
  path: '/account/topics/hidden',
  template: StretchedWidthTemplate,
  main: LazyOwnTopicsMain,
  help: true,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const ownTopicsRejectedRoute = {
  path: '/account/topics/rejected',
  template: StretchedWidthTemplate,
  main: LazyOwnTopicsMain,
  help: true,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const ownTopicsPendingRoute = {
  path: '/account/topics/pending',
  template: StretchedWidthTemplate,
  main: LazyOwnTopicsMain,
  help: true,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const availabilityRoute = {
  path: '/account/availability/',
  template: StretchedWidthTemplate,
  main: LazyAvaibilityMain,
  help: true,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const profileEditRoute = {
  path: '/account/profile/edit/',
  template: StretchedWidthTemplate,
  main: LazyProfileEditMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: false,
  help: true,
};

export const faqRoute = {
  path: '/faq/',
  template: WithSidebarTemplate,
  main: LazyFAQMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const supportRoute = {
  path: '/support/',
  template: WithSidebarTemplate,
  main: LazySupportMain,
  redirect: standardAuthenticatedPageRedirect,
  scrollToTop: true,
};

export const oauthRedirect = {
  path: '/oauth2/redirect/',
  template: BlankTemplate,
  main: LazyOauthRedirect,
};

export const serverErrorRoute = {
  path: '/server-error/',
  template: BlankTemplate,
  main: LazyServerErrorMain,
};

export const clientErrorRoute = {
  path: '/client-error/',
  template: BlankTemplate,
  main: LazyClientErrorMain,
};

export const lightRoute = {
  path: '/light/',
  template: BlankTemplate,
  main: LazyLightMain,
};

export const blankRoute = {
  path: '/blank/',
  template: BlankTemplate,
  main: LazyBlankMain,
};

export const blankWithDesktopSidebarRoute = {
  path: '/blank-with-desktop-sidebar/',
  template: BlankWithDesktopSideBarTemplate,
  main: LazyBlankMain,
};

export const withSidebarRoute = {
  path: '/with-nav/',
  template: WithSidebarTemplate,
  main: LazyBlankMain,
};

export const stretchedNavRoute = {
  path: '/streteched/',
  template: StretchedWidthTemplate,
  main: LazyBlankMain,
};

export const fullWidthNavRoute = {
  path: '/full-width/',
  template: FullWidthTemplate,
  main: LazyBlankMain,
};

export const fullscreenNavRoute = {
  path: '/fullscreen/',
  template: FullscreenTemplate,
  main: LazyBlankMain,
};

// DON'T DEFINED NEW ROUTES UNDER THIS LINE

export const notFoundRoute = {
  path: '*',
  template: WithSidebarTemplate,
  main: LazyNotFoundMain,
  scrollToTop: true,
};

export default {
  homeRoute,
  peopleFeedRoute,
  signupRoute,
  loginRoute,
  loginSupportRoute,
  retrieveUsernameRoute,
  loginLinkRoute,
  contactRoute,
  passwordSupportRoute,
  resetPasswordRoute,
  topicRoute,
  topicEditRoute,
  topicInviteRoute,
  newTopicRoute,
  newTopicNoPermissionRoute,
  braindatesRoute,
  pendingBraindatesRoute,
  confirmedBraindatesRoute,
  pastBraindatesRoute,
  braindateRoute,
  conversationRoute,
  preferencesRoute,
  userRoute,
  userInviteRoute,
  connectOnInterestRoute,
  profileRoute,
  ownTopicsRoute,
  ownTopicsActiveRoute,
  ownTopicsHiddenRoute,
  ownTopicsRejectedRoute,
  ownTopicsPendingRoute,
  availabilityRoute,
  profileEditRoute,
  faqRoute,
  supportRoute,
  oauthRedirect,
  serverErrorRoute,
  clientErrorRoute,
  lightRoute,
  expressionOfGratitudeRoute,
  newExpressionOfGratitudeRoute,
  newExpressionOfGratitudeWithBraindateRoute,
  newExpressionOfGratitudeWithBraindateAndUserRoute,
  expressionOfGratitudeDetailsRoute,

  blankRoute,
  blankWithDesktopSidebarRoute,
  withSidebarRoute,
  stretchedNavRoute,
  fullWidthNavRoute,
  fullscreenNavRoute,

  notFoundRoute,
};
